import React from 'react';
import PropTypes from 'prop-types';

import Icon from '../../base/Icon';
import ExpandableToast from '../../modules/ExpandableToast';

export const Footer = ({
  disclaimerFootnotes,
  customDisclaimers,
  isAmp,
  legalCopy,
  appendFooter,
  showExpandableToast,
}) => {
  const nmxTemplateVersion = '<%=nmxTemplateVersion%>';
  const basePath = '<%=basePath%>';
  const FOOTER_DISCLAIMER = (
    <div className="nmx-row nmx-row--align-center">
      <div
        className={`nmx-col nmx-footer__disclaimer-content ${
          (disclaimerFootnotes && disclaimerFootnotes.length > 0)
          || (customDisclaimers && customDisclaimers.length > 0)
            ? 'has-custom'
            : ''
        }`}>
        <h4 className="nmx-assistive-text">Footer Disclaimer</h4>
        <p>
          <small className="nmx-small">
            Northwestern Mutual is the marketing name for The Northwestern
            Mutual Life Insurance Company and its subsidiaries. Life and
            disability insurance, annuities, and life insurance with
            long&#45;term care benefits are issued by The Northwestern Mutual
            Life Insurance Company, Milwaukee, WI (NM). Long&#45;term care
            insurance is issued by Northwestern Long Term Care Insurance
            Company, Milwaukee, WI, (NLTC) a subsidiary of NM. Investment
            brokerage services are offered through{' '}
            <strong>Northwestern Mutual Investment Services, LLC (NMIS)</strong>{' '}
            a subsidiary of NM, broker&#45;dealer, registered investment
            adviser, and member{' '}
            <strong>
              <a
                href="https://www.finra.org/"
                target="_blank"
                rel="nofollow noopener noreferrer"
                className="nmx-link">
                FINRA
              </a>
            </strong>{' '}
            and{' '}
            <strong>
              <a
                href="https://www.sipc.org/"
                target="_blank"
                rel="nofollow noopener noreferrer"
                className="nmx-link">
                SIPC
              </a>
            </strong>
            . Investment advisory and trust services are offered through
            Northwestern Mutual Wealth Management Company&#174; (NMWMC),
            Milwaukee, WI, a subsidiary of NM and a federal savings bank.
            Products and services referenced are offered and sold only by
            appropriately appointed and licensed entities and financial advisors
            and professionals.{' '}
            <strong>
              Not all products and services are available in all states. Not all
              Northwestern Mutual representatives are advisors. Only those
              representatives with &#34;Advisor&#34; in their title or who
              otherwise disclose their status as an advisor of NMWMC are
              credentialed as NMWMC representatives to provide investment
              advisory services.
            </strong>{' '}
            {isAmp && (
              <a
                href="https://www.google.com/policies/privacy/partners/"
                target="_blank"
                rel="nofollow noopener noreferrer"
                className="nmx-link">
                Google Privacy and Terms.
              </a>
            )}
          </small>
        </p>
      </div>
    </div>
  );

  return (
    // TODO: remove class "nmx-base" once nmx-template 1.0.x is implemented and "nmx-base" is added to body tag
    <footer className="nmx-footer nmx-base">
      <div className="nmx-container">
        <h2 className="nmx-assistive-text">
          Northwestern Mutual Website Footer
        </h2>
        <section className="nmx-footer-section nmx-footer__disclaimer">
          {(disclaimerFootnotes && disclaimerFootnotes.length > 0)
          || (customDisclaimers && customDisclaimers.length > 0) ? (
            /* If disclaimerFootnotes or customDisclaimer, then put custom content on left, stock disclaimer on right */
            <div className="nmx-row nmx-row--align-center">
              <div className="nmx-col nmx-col-xsmall-12 nmx-col-medium-6 nmx-footer__disclaimer-content-col">
                {disclaimerFootnotes && disclaimerFootnotes.length > 0 && (
                  <div className="nmx-row nmx-row--align-center">
                    <div className="nmx-col nmx-footer__disclaimer-content nmx-footer__disclaimer-content--custom-footnotes has-custom">
                      {disclaimerFootnotes.map((disclaimerFootnote, index) => (
                        <p
                          className="footnote-item"
                          key={`disclaimer-footnote-${index}`}>
                          <small className="nmx-small">
                            {disclaimerFootnote}
                          </small>
                        </p>
                      ))}
                    </div>
                  </div>
                )}

                {customDisclaimers && customDisclaimers.length > 0 && (
                  <div className="nmx-row nmx-row--align-center">
                    <div className="nmx-col nmx-footer__disclaimer-content nmx-footer__disclaimer-content--custom-disclaimers has-customnmx-col nmx-footer__disclaimer-content has-custom">
                      {customDisclaimers.map((customDisclaimer, index) => (
                        <p
                          className="footnote-item"
                          key={`custom-disclaimer-footnote-${index}`}>
                          <small className="nmx-small">
                            {customDisclaimer}
                          </small>
                        </p>
                      ))}
                    </div>
                  </div>
                )}
              </div>
              <div className="nmx-col nmx-col-xsmall-12 nmx-col-medium-6 nmx-footer__disclaimer-content-col">
                {FOOTER_DISCLAIMER}
              </div>
            </div>
            ) : (
            /* Otherwise, let custom disclaimer span full-width broken up into 2 cols */
              <div className="nmx-row nmx-row--align-center">
                {FOOTER_DISCLAIMER}
              </div>
            )}
        </section>

        <section className="nmx-footer-section nmx-footer__nav-container">
          <div className="nmx-row nmx-row--align-left">
            <div className="nmx-col">
              <h6 className="nmx-footer-logo-container">
                <a className="nmx-footer-logo" href="/" id="footer-logo">
                  Northwestern Mutual
                  {/* TODO: clean up SVG markup (change "NW" to "nm" clean up IDs, descriptions, titles, etc.) */}
                </a>
                <meta
                  itemProp="image"
                  content={`${basePath}/template/assets/${nmxTemplateVersion}/images/logo-horizontal-navy.svg`}
                />
                {/* amp requires us to use amp-img */}
                {isAmp ? (
                  <amp-img
                    className="nmx-print"
                    width="332"
                    height="40"
                    src={`${basePath}/template/assets/${nmxTemplateVersion}/images/logo-horizontal-navy.svg`}
                    alt="Northwestern Mutual"
                  />
                ) : (
                  <img
                    className="nmx-print"
                    src={`${basePath}/template/assets/${nmxTemplateVersion}/images/logo-horizontal-navy.svg`}
                    alt="Northwestern Mutual"
                  />
                )}
              </h6>
            </div>
          </div>
          <div className="nmx-row">
            <div className="nmx-col nmx-col-nested nmx-col-xsmall-12 nmx-col-medium-8 nmx-col-large-5">
              <nav
                itemScope
                itemType="https://schema.org/SiteNavigationElement"
                className="nmx-footer-nav"
                id="nmx-footer-nav">
                <h4 className="nmx-assistive-text">Footer Navigation</h4>
                <div className="nmx-row">
                  <div className="nmx-col nmx-col-xsmall-12 nmx-col-small-6 nmx-col-medium-4">
                    <ul role="menu" className="reduced">
                      <li role="none" className="nmx-footer-nav-link-container">
                        <a
                          role="menuitem"
                          className="nmx-footer-nav-link"
                          href={`${basePath}/who-we-are`}>
                          About Us
                        </a>
                      </li>
                      <li role="none" className="nmx-footer-nav-link-container">
                        <a
                          role="menuitem"
                          className="nmx-footer-nav-link"
                          target="_blank"
                          rel="noopener noreferrer"
                          href="https://news.northwesternmutual.com">
                          Newsroom
                        </a>
                      </li>
                      <li role="none" className="nmx-footer-nav-link-container">
                        <a
                          role="menuitem"
                          className="nmx-footer-nav-link"
                          href={`${basePath}/careers`}>
                          Careers
                        </a>
                      </li>
                      <li role="none" className="nmx-footer-nav-link-container">
                        <a
                          role="menuitem"
                          className="nmx-footer-nav-link"
                          href={`${basePath}/security-and-privacy`}>
                          Information Protection
                        </a>
                      </li>
                    </ul>
                  </div>
                  <div className="nmx-col nmx-col-xsmall-12 nmx-col-small-6 nmx-col-medium-4">
                    <ul role="menu" className="reduced">
                      <li role="none" className="nmx-footer-nav-link-container">
                        <a
                          role="menuitem"
                          className="nmx-footer-nav-link"
                          href={`${basePath}/business-services`}>
                          Business Services
                        </a>
                      </li>
                      <li role="none" className="nmx-footer-nav-link-container">
                        <a
                          role="menuitem"
                          className="nmx-footer-nav-link"
                          target="_blank"
                          rel="noopener noreferrer"
                          href={`${basePath}/contact-us`}>
                          Contact Us
                        </a>
                      </li>
                      <li role="none" className="nmx-footer-nav-link-container">
                        <a
                          role="menuitem"
                          className="nmx-footer-nav-link"
                          href={`${basePath}/faq`}>
                          FAQs
                        </a>
                      </li>
                      <li role="none" className="nmx-footer-nav-link-container">
                        <a
                          role="menuitem"
                          className="nmx-footer-nav-link"
                          href={`${basePath}/legal-information`}>
                          Legal Notice
                        </a>
                      </li>
                    </ul>
                  </div>
                  <div className="nmx-col nmx-col-xsmall-12 nmx-col-small-6 nmx-col-small--offset-6 nmx-col-medium-4 nmx-col-medium--offset-0">
                    <ul role="menu" className="reduced">
                      <li role="none" className="nmx-footer-nav-link-container">
                        <a
                          role="menuitem"
                          className="nmx-footer-nav-link"
                          href={`${basePath}/sitemap`}>
                          Sitemap
                        </a>
                      </li>
                      <li role="none" className="nmx-footer-nav-link-container">
                        <a
                          role="menuitem"
                          className="nmx-footer-nav-link"
                          target="_blank"
                          rel="noopener noreferrer"
                          href={`${basePath}/privacy-notices`}>
                          Privacy Notices
                        </a>
                      </li>
                    </ul>
                  </div>
                </div>
              </nav>
            </div>
            <div className="nmx-col nmx-col-xsmall-12 nmx-col-medium-4 nmx-col-large-3">
              <h4
                className="nmx-footer-social__links-heading"
                aria-label="Footer Social Media Links">
                Connect with us
              </h4>
              <ul className="reduced nmx-footer-social__links-container">
                <li className="nmx-footer-social__link-item">
                  <a
                    href="https://www.facebook.com/northwesternmutual"
                    target="_blank"
                    rel="nofollow noopener noreferrer"
                    className="nmx-footer-social__link social-link-facebook"
                    aria-label="Connect with Northwestern Mutual on Facebook">
                    <Icon variant='facebook' />
                  </a>
                </li>
                <li className="nmx-footer-social__link-item">
                  <a
                    href="https://twitter.com/nm_financial"
                    target="_blank"
                    rel="nofollow noopener noreferrer"
                    className="nmx-footer-social__link social-link-twitter"
                    aria-label="Follow Northwestern Mutual on Twitter">
                    <Icon variant='twitter' />
                  </a>
                </li>
                <li className="nmx-footer-social__link-item">
                  <a
                    href="https://www.linkedin.com/company/northwestern-mutual"
                    target="_blank"
                    rel="nofollow noopener noreferrer"
                    className="nmx-footer-social__link social-link-linkedin"
                    aria-label="Visit Northwestern Mutual on LinkedIn">
                    <Icon variant='linkedin' />
                  </a>
                </li>
                <li className="nmx-footer-social__link-item">
                  <a
                    href="https://instagram.com/northwesternmutual"
                    target="_blank"
                    rel="nofollow noopener noreferrer"
                    className="nmx-footer-social__link social-link-instagram"
                    aria-label="Follow Northwestern Mutual on Instagram">
                    <Icon variant='instagram' />
                  </a>
                </li>
                <li className="nmx-footer-social__link-item">
                  <a
                    href="https://www.youtube.com/northwesternmutual"
                    target="_blank"
                    rel="nofollow noopener noreferrer"
                    className="nmx-footer-social__link social-link-youtube"
                    aria-label="Connect with Northwestern Mutual on YouTube">
                    <Icon variant='youtube' />
                  </a>
                </li>
              </ul>
            </div>
            <div className="nmx-col nmx-col-xsmall-12 nmx-col-large-4 nmx-footer-fafa__container">
              <h4 className="nmx-h5 nmx-footer-fafa__heading">
                Over 6,400 Financial Advisors and Professionals Nationwide
              </h4>
              <a
                href="/find-a-financial-advisor/"
                className="nmx-link nmx-link--stand-alone">
                Find an Advisor
              </a>
            </div>
          </div>
        </section>
        <section className="nmx-footer-section nmx-footer__copyright-container">
          <h4 className="nmx-assistive-text">Footer Copyright</h4>
          <div className="nmx-row nmx-row--align-center">
            <div className="nmx-col nmx-col-xsmall-12 nmx-col-medium-6">
              {legalCopy /* TODO: format needs checking/work, this was pulled in from nmx-static... still being used? */ ? (
                { legalCopy }
              ) : (
                <p>
                  <small>
                    Copyright &#169; {new Date().getFullYear()} The Northwestern
                    Mutual Life Insurance Company, Milwaukee, WI. All Rights
                    Reserved. Northwestern Mutual is the marketing name for The
                    Northwestern Mutual Life Insurance Company and its
                    subsidiaries.
                  </small>
                </p>
              )}
            </div>
            <div className="nmx-col nmx-col-xsmall-12 nmx-col-medium-6">
              {appendFooter || <React.Fragment />}
              {/* TODO: format needs checking/work, this was pulled in from nmx-static... still being used? */}
            </div>
          </div>
        </section>
      </div>
      {showExpandableToast && (
        <ExpandableToast
          heading="Keeping you safe through COVID&#8211;19."
          text='["Our advisors are here for you and ready to help. We&#39;ll create a plan and give ongoing advice without you ever having to leave your home&#8212;by phone, email, or through virtual meetings. Plus, you&#39;ll get online access to your plan remotely anytime, on any device.", "Your health and safety are important to us. If you need insurance, we may be able to delay your medical exams without delaying your coverage. Connect with a financial advisor to find out more."]'
          storage_var="showCovidToast"
        />
      )}
    </footer>
  );
};

Footer.propTypes = {
  disclaimerFootnotes: PropTypes.any,
  customDisclaimers: PropTypes.any,
  isAmp: PropTypes.bool,
  legalCopy: PropTypes.any,
  appendFooter: PropTypes.any,
  showExpandableToast: PropTypes.bool,
};

Footer.defaultProps = {
  isAmp: false,
  showExpandableToast: false,
};

export default Footer;
